/**
 * useFindingSlices — load and window-render a cine window per finding.
 *
 * Mirrors the load pattern from FindingThumbnails but:
 * - Uses anatomy-specific window presets (presetForFinding) instead of DICOM defaults
 * - Renders at native resolution (capped at 512px) for gallery tile quality
 * - Returns a window of neighbor slices (RADIUS each side) in slice order so the
 *   gallery can ping-pong cine through them. frames[centerIdx] is the key slice.
 *
 * NO markings, NO bboxes — clean pixels only (510(k) constraint).
 *
 * Source of that constraint, for traceability. It is a device claim, not a
 * design preference, and this is the only place in this repo that cites it —
 * FindingPreviewTile, FindingPreviewModal, useFindingSliceWindow,
 * RetroAgreementStage and aiJumpPlaneGate all enforce it and point here.
 *
 * It is a TRIAGE-device claim, and it is per-device rather than global. Every
 * a2z triage submission carries it, with the same two document IDs:
 *
 *   a2z-regulatory/reg-fda-{abdo,chest,head,omni,unified}-triage-submission
 *     final-submission-package/markdown-sources/DOC-0002_510(k)_Summary.md
 *       — key images "do not contain any marking of the findings"
 *     final-submission-package/markdown-sources/DOC-0311_Risk_Assessment.md
 *       — the device "does not provide any marking on the output preview"
 *
 * DOC-0801_eSTAR_Device_Description.md repeats the DOC-0002 wording. Read the
 * submission for the device in question rather than relying on this paraphrase;
 * those documents are the source of truth and this comment is a pointer.
 *
 * This demo renders non-triage devices too, and they do NOT inherit the above.
 * reg-ce-drafter-submission carries no equivalent marking claim — drafter is a
 * CE report-drafting device, a different class with different constraints. So a
 * new device arriving here needs its own answer to "may previews carry
 * markings", read from ITS submission; do not assume this constraint applies,
 * and do not assume it does not. If a device ever permits markings, the gate
 * belongs on device identity at the call site, not on loosening this loader —
 * the whole point of keeping the clean-pixels path device-blind is that it
 * cannot leak a marking for a device that forbids one.
 *
 * Two consequences worth keeping in view when editing any preview surface:
 * cine and per-anatomy windowing are NOT finding markings and are allowed; and
 * the on-screen disclaimer string is itself a regulatory claim, so its final
 * wording is reg-owned and does not get edited here.
 */

import { useState, useEffect, useMemo } from "react";
import { initializeCornerstone } from "../utils/dicomLoader";
import {
  buildS3ImageIds,
  fetchStudyMetadata,
  StudyNotCanonicalizedError,
  type DicomInstanceInfo,
  type StudyMetadataResponse,
} from "../utils/dicomMetadataLoader";
import { getApiBaseUrl } from "../config/api";
import { renderPixelsToCanvas, type PixelDataArray } from "../utils/renderPixelsToCanvas";
import { presetForFinding } from "../utils/findingWindow";
import * as cornerstone from "@cornerstonejs/core";
import type { GalleryFinding } from "./useStudyQueries";
import { registerCdnToken } from "../utils/cdnTokens";

/** Seed every finding to loading:true — used for the render-phase reset. */
function seedAllLoading(fns: GalleryFinding[]): Record<string, FindingSliceState> {
  const map: Record<string, FindingSliceState> = {};
  for (const f of fns) map[f.condition] = { frames: [], loading: true };
  return map;
}

// Gallery tiles render at up to MIN_TILE (180px), but capping the canvas at 512
// gives a sharp retina image without needing to fetch a separate resolution.
const GALLERY_OUTPUT_SIZE = 512;

// Cine window half-width: load up to RADIUS neighbor slices on each side of the
// key slice (so up to 2*RADIUS+1 = 7 frames), clamped to the series bounds.
const RADIUS = 3;

export interface FindingSliceState {
  frames: string[]; // slice-ordered cine window; frames[centerIdx] is the key slice
  loading: boolean;
  /** The key slice itself, for callers that show one still frame. */
  keyFrame?: string;
}

/**
 * Load the key slice for each finding and render it to a dataURL
 * windowed by anatomy-specific preset (not DICOM default).
 *
 * @param studyId  Study identifier
 * @param deviceId Device identifier (reserved for v2 / Task 8 call convention)
 * @param findings Ordered list from selectGalleryFindings
 * @param images   The study's images already read through a page's own images
 *                 route (a device panel's), whose callers may have no access to
 *                 get_study_bbox; omit to read them by study id. Pass it only
 *                 once read: an omitted value means "read by study id".
 * @returns Record mapping condition → { frames, loading }
 */
export function useFindingSlices(
  studyId: string,
  _deviceId: string, // reserved — unused in v1 but kept for Task 8 call convention
  findings: GalleryFinding[],
  images?: StudyMetadataResponse,
): Record<string, FindingSliceState> {
  const [sliceMap, setSliceMap] = useState<Record<string, FindingSliceState>>({});

  // ── Render-phase reset ────────────────────────────────────────────────────
  // Track the last key we seeded. When findingsKey changes, reset synchronously
  // in the same render so the *new* conditions start as loading:true. Without
  // this, the render that commits the new key still sees the old sliceMap
  // (which may have loading:false entries from the previous study/findings),
  // producing a brief "no preview" flash before the async effect re-seeds.
  // This is React's "adjust state on prop change" pattern (getDerivedStateFromProps).
  const [lastKey, setLastKey] = useState("");

  // Stable signature — avoids reload on cine-timer re-renders that produce new
  // array references without actually changing which findings we need to load.
  const findingsKey = useMemo(
    () => findings.map((f) => `${f.condition}|${f.topSlice ?? ""}`).join(","),
    [findings],
  );

  // Render-phase reset: when the key changes, reset the map synchronously so no
  // stale loading:false entries from the old findings are visible in this render.
  if (findingsKey !== lastKey) {
    setLastKey(findingsKey);
    setSliceMap(seedAllLoading(findings));
  }

  // findingsKey is the intentional dependency: it is content-derived
  // (condition|topSlice per finding), so it is stable across cine-tick
  // re-renders that produce new `findings` array refs, and it changes
  // exactly when a load input (condition or topSlice) changes. Depending on
  // the `findings` array identity would reload on every parent re-render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Capture the prop value at effect-trigger time so the closure dependency
    // is explicit — `fns` is the snapshot that matches `findingsKey`.
    const fns = findings;

    if (!studyId || fns.length === 0) {
      setSliceMap({});
      return;
    }

    const token = document.cookie.match(/a2z_auth_token=([^;]+)/)?.[1] ?? "";
    const userId = document.cookie.match(/a2z_user_id=([^;]+)/)?.[1] ?? "";
    let cancelled = false;

    // Initialise all findings to loading state immediately
    const initialMap: Record<string, FindingSliceState> = {};
    for (const f of fns) {
      initialMap[f.condition] = { frames: [], loading: true };
    }
    setSliceMap(initialMap);

    const source: Promise<{
      cdnBaseUrl: string | undefined;
      s3Series: Record<string, any[]> | undefined;
    }> = images
      ? Promise.resolve({ cdnBaseUrl: images.canonical_cdn_base_url, s3Series: images.series })
      : Promise.all([
          fetch(`${getApiBaseUrl()}/api/get_study_bbox`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ user_id: userId, study_id: studyId }),
          }).then((r) => r.json()),
          fetchStudyMetadata(userId, studyId, token).catch((e) => {
            if (e instanceof StudyNotCanonicalizedError) return null;
            throw e;
          }),
        ]).then(([bboxResp, metaResp]) => {
          registerCdnToken(bboxResp.canonical_cdn_token);
          return {
            cdnBaseUrl: bboxResp.canonical_cdn_base_url ?? metaResp?.canonical_cdn_base_url,
            s3Series: metaResp?.series ?? bboxResp.s3_series ?? {},
          };
        });

    source
      .then(async ({ cdnBaseUrl, s3Series }) => {
        if (cancelled) return;

        if (!cdnBaseUrl || !s3Series) {
          // Study not canonicalized or no DICOM bytes available — mark all done with no frames
          if (!cancelled) {
            const done: Record<string, FindingSliceState> = {};
            for (const f of fns) {
              done[f.condition] = { frames: [], loading: false };
            }
            setSliceMap(done);
          }
          return;
        }

        await initializeCornerstone();
        if (cancelled) return;

        // Load every finding concurrently. Each finding renders center-first
        // then expands to its cine neighbors, and awaits its own center before
        // enqueuing neighbors — so across findings the key slices are naturally
        // prioritized and all land in roughly one slice-load time instead of
        // serializing (10 findings × up to 7 slices used to load end-to-end).
        // Real concurrency is bounded downstream — Cornerstone's image-load /
        // decode worker pool throttles, and the CDN is HTTP/2 — so firing all
        // findings at once queues gracefully rather than flooding. Each loader
        // publishes only its own condition via a functional setSliceMap update,
        // so concurrent updates don't clobber each other.
        await Promise.all(
          fns.map(async (finding) => {
            if (cancelled) return;

            // topSlice may carry a ".dcm" suffix from the bbox response
            let sliceUid = finding.topSlice;
            if (!sliceUid) {
              setSliceMap((prev) => ({
                ...prev,
                [finding.condition]: { frames: [], loading: false },
              }));
              return;
            }
            if (sliceUid.endsWith(".dcm")) sliceUid = sliceUid.slice(0, -4);

            try {
              // Locate the key slice AND the series array it belongs to. The
              // series arrays arrive pre-sorted by z_position (anatomical order)
              // from the backend — same contract buildS3ImageIds relies on — so
              // we use them as-is. (instance_number is acquisition/storage order
              // and can run opposite to z; never sort by it here.)
              let seriesInstances: DicomInstanceInfo[] | null = null;
              let keyIdx = -1;
              for (const instances of Object.values(s3Series)) {
                const idx = (instances as any[]).findIndex(
                  (inst: any) => inst.instance_uid === sliceUid,
                );
                if (idx !== -1) {
                  seriesInstances = instances as DicomInstanceInfo[];
                  keyIdx = idx;
                  break;
                }
              }

              if (!seriesInstances || keyIdx === -1) {
                setSliceMap((prev) => ({
                  ...prev,
                  [finding.condition]: { frames: [], loading: false },
                }));
                return;
              }

              // Slice-ordered window [keyIdx-RADIUS, keyIdx+RADIUS] clamped to bounds.
              const start = Math.max(0, keyIdx - RADIUS);
              const end = Math.min(seriesInstances.length - 1, keyIdx + RADIUS);
              const windowInstances = seriesInstances.slice(start, end + 1);
              const centerIdx = keyIdx - start; // key's position within the window

              const preset = presetForFinding(finding.condition);

              // Render one instance → windowed dataURL via the anatomy preset.
              const renderInstance = async (inst: DicomInstanceInfo): Promise<string> => {
                const [imageId] = buildS3ImageIds(cdnBaseUrl, [inst]);
                const image = await cornerstone.imageLoader.loadAndCacheImage(imageId);
                const pixelData = image.getPixelData() as PixelDataArray;
                const canvas = renderPixelsToCanvas(
                  pixelData,
                  image.rows,
                  image.columns,
                  preset.center,
                  preset.width,
                  GALLERY_OUTPUT_SIZE,
                );
                return canvas.toDataURL();
              };

              // Center-first: render the key slice and publish it immediately so the
              // tile shows a frame right away (length 1 → static until neighbors land).
              const frames: (string | undefined)[] = Array.from({
                length: windowInstances.length,
              });
              frames[centerIdx] = await renderInstance(windowInstances[centerIdx]);
              if (cancelled) return;
              setSliceMap((prev) => ({
                ...prev,
                [finding.condition]: {
                  frames: [frames[centerIdx] as string],
                  loading: false,
                  keyFrame: frames[centerIdx],
                },
              }));

              // Expand outward (±1, ±2, …) so the loaded set stays a contiguous,
              // hole-free, slice-ordered block around the key at every publish step.
              for (let off = 1; off <= RADIUS; off++) {
                for (const i of [centerIdx - off, centerIdx + off]) {
                  if (i < 0 || i >= windowInstances.length) continue;
                  frames[i] = await renderInstance(windowInstances[i]);
                  if (cancelled) return;
                  // Publish the contiguous run that includes the center.
                  let lo = centerIdx;
                  let hi = centerIdx;
                  while (lo - 1 >= 0 && frames[lo - 1] !== undefined) lo--;
                  while (hi + 1 < frames.length && frames[hi + 1] !== undefined) hi++;
                  const contiguous = frames.slice(lo, hi + 1) as string[];
                  setSliceMap((prev) => ({
                    ...prev,
                    [finding.condition]: {
                      frames: contiguous,
                      loading: false,
                      keyFrame: frames[centerIdx],
                    },
                  }));
                }
              }
            } catch {
              setSliceMap((prev) => ({
                ...prev,
                [finding.condition]: { frames: [], loading: false },
              }));
            }
          }),
        );
      })
      .catch(() => {
        if (!cancelled) {
          const done: Record<string, FindingSliceState> = {};
          for (const f of fns) {
            done[f.condition] = { frames: [], loading: false };
          }
          setSliceMap(done);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [studyId, findingsKey, images]);

  return sliceMap;
}
